import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { DropdownMenu } from 'radix-ui';
import { Languages, LogOut, Palette, ShieldCheck, Smartphone } from 'lucide-react';
import { useMe } from '@/api/auth';
import { useAuth } from '@/auth/AuthProvider';
import { setLanguage, type Language } from '@/i18n';
import { THEMES, useTheme, type ThemePref } from '@/lib/theme';
import { AppearanceDialog, THEME_ICON, ThemeLabel, originOf, themeNameKey } from '@/components/domain/Appearance';
import { Menu, MenuItem, MenuLabel, MenuRadio, MenuSeparator } from '@/components/ui/Menu';
import { cn } from '@/lib/cn';

export function initials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toLocaleUpperCase('tr');
}

export function UserMenu({ compact }: { compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { pref, setPref } = useTheme();
  const [appearance, setAppearance] = useState(false);
  return (
    <>
      <Menu
        align="start"
        label={t('shell.userMenu')}
        trigger={
          <button
            type="button"
            className={cn(
              'flex w-full min-w-0 items-center gap-2.5 rounded-[var(--radius-ctl)] p-1.5 text-left hover:bg-surface-3',
              compact && 'w-auto',
            )}
            aria-label={t('shell.userMenu')}
          >
            <span className="mono flex size-8 shrink-0 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-bg">
              {initials(me?.displayName)}
            </span>
            {!compact && (
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{me?.displayName ?? '…'}</span>
                <span className="block truncate text-xs text-ink-3">{me?.email}</span>
              </span>
            )}
          </button>
        }
      >
        <MenuLabel>{t('shell.language')}</MenuLabel>
        <MenuRadio
          value={i18n.language.startsWith('en') ? 'en' : 'tr'}
          onValueChange={(v) => setLanguage(v as Language)}
          options={[
            { value: 'tr', label: 'Türkçe' },
            { value: 'en', label: 'English' },
          ]}
        />
        <MenuSeparator />
        <MenuLabel>{t('shell.theme')}</MenuLabel>
        <MenuRadio
          value={pref}
          onValueChange={(v) => setPref(v as ThemePref)}
          options={THEMES.map(({ id }) => ({ value: id, label: <ThemeLabel id={id} /> }))}
        />
        <MenuItem icon={<Palette />} onSelect={() => setAppearance(true)}>{t('appearance.open')}</MenuItem>
        <MenuSeparator />
        <DropdownMenu.Item asChild>
          <Link to="/my/devices" className="flex items-center gap-2 rounded-[var(--radius-ctl)] px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-3">
            <Smartphone className="size-4 text-ink-3" />
            {t('nav.devices')}
          </Link>
        </DropdownMenu.Item>
        <DropdownMenu.Item asChild>
          <Link to="/privacy" className="flex items-center gap-2 rounded-[var(--radius-ctl)] px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-3">
            <ShieldCheck className="size-4 text-ink-3" />
            {t('privacy.menu')}
          </Link>
        </DropdownMenu.Item>
        <MenuItem
          icon={<LogOut />}
          onSelect={() => {
            void logout().then(() => navigate('/login'));
          }}
        >
          {t('shell.logout')}
        </MenuItem>
      </Menu>
      <AppearanceDialog open={appearance} onOpenChange={setAppearance} />
    </>
  );
}

/** Small language + theme toggles for public / focus layouts. */
export function QuickPrefs() {
  const { t, i18n } = useTranslation();
  const { pref, cycle } = useTheme();
  const lang = i18n.language.startsWith('en') ? 'en' : 'tr';
  const ThemeIcon = THEME_ICON[pref];
  const mode = t(themeNameKey(pref));
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => setLanguage(lang === 'tr' ? 'en' : 'tr')}
        className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-ctl)] px-2 text-xs font-medium text-ink-2 hover:bg-surface-3 hover:text-ink"
        aria-label={t('shell.switchLanguage')}
        title={t('shell.switchLanguage')}
      >
        <Languages className="size-4" />
        <span className="mono uppercase">{lang}</span>
      </button>
      <button
        type="button"
        onClick={(e) => cycle(originOf(e))}
        className="inline-flex size-8 items-center justify-center rounded-[var(--radius-ctl)] text-ink-2 hover:bg-surface-3 hover:text-ink"
        aria-label={t('shell.themeToggle', { mode })}
        title={t('shell.themeToggle', { mode })}
      >
        <ThemeIcon className="size-4" />
      </button>
    </div>
  );
}
